import { useCallback, useEffect } from 'react';

export type EventBusMessage<T extends string = string, M = unknown> = {
    topic: T;
    message: M;
};

type EventAction<T extends EventBusMessage> = {
    topic: T['topic'];
    data: T['message'];
};

type Filter<T extends EventBusMessage> = T['topic'] | ((event: EventAction<T>) => boolean);

type Subscriber<T extends EventBusMessage> = [Filter<T>, (event: EventAction<T>) => void];

const subscribers = new Set<Subscriber<EventBusMessage>>();

export const busSubscribe = <T extends EventBusMessage>(
    filter: Filter<T>,
    callback: (event: EventAction<T>) => void,
): (() => void) => {
    const newSubscriber: Subscriber<T> = [filter, callback];
    subscribers.add(newSubscriber as Subscriber<EventBusMessage>);

    return () => {
        subscribers.delete(newSubscriber as Subscriber<EventBusMessage>);
    };
};

export const busDispatch = <T extends EventBusMessage>(topic: T['topic'], message: T['message']): void => {
    const eventAction: EventAction<T> = { topic, data: message };

    subscribers.forEach(([filter, callback]) => {
        try {
            if (
                (typeof filter === 'string' && filter === eventAction.topic) ||
                (typeof filter === 'function' && filter(eventAction as EventAction<EventBusMessage>))
            ) {
                callback(eventAction as EventAction<EventBusMessage>);
            }
        } catch (error) {
            window.console.error(`Error in event bus subscriber for topic "${topic}":`, error);
        }
    });
};

export const useEventBus = <T extends EventBusMessage>(
    topic: T['topic'],
    callback: (message: T['message']) => void,
    deps: unknown[] = [],
): void => {
    const memoizedCallback = useCallback(
        (event: EventAction<T>) => {
            callback(event.data);
        },
        [callback, ...deps],
    );

    useEffect(() => {
        return busSubscribe<T>(topic, memoizedCallback);
    }, [topic, memoizedCallback]);
};
